import { AFFILIATE, PHYSICIAN_TEAM, SKILLED_NURSING_FACILITY } from 'constants/locationTypes';
import type { IntegrationType } from 'services/api/fields/admin/client';

import Classification, { ClassificationOptions } from './Classification';
import ClientGroupType, { ClientGroupTypeOptions } from './ClientGroupType';
import Collaboration from './Collaboration';
import GroupType, { GroupTypeOptions } from './GroupType';

export enum ClientType {
  ADMIN = 'Admin',
  AFFILIATE = 'Affiliate',
  HEALTH_SYSTEM = 'Health System',
  INPATIENT_POST_ACUTE_CARE = 'Inpatient Post Acute Care',
  OUTPATIENT_POST_ACUTE_CARE = 'Outpatient Post Acute Care',
  PAYOR = 'Payor',
  PHYSICIAN_GROUP = 'Physician Group',
}

export enum ImportType {
  IMPORTED_PATIENT = 'imported_patient',
  PATIENT = 'patient',
}

export type ProductAccess = {
  [key in
    | 'traditionalOlio'
    | 'olioSummarization'
    | 'referralSummarization'
    | 'dischargeCalendar'
    | 'admissions'
    | 'inboundReferrals'
    | 'preferredProviders']: {
    enabled: boolean;
  };
};

export interface ClientOptions {
  id: string;
  children: Partial<ClientOptions>[];
  clientType: ClientType;
  clientGroupTypes: Partial<ClientGroupTypeOptions>[];
  collaboratorIds: string[];
  unownedCollaborations: Partial<Collaboration>[];
  episodeClassifications: Partial<ClassificationOptions>[];
  enabledProviderTypes: Partial<GroupTypeOptions>[];
  groupTypeDisplayNames: string[];
  groupTypes: Partial<GroupTypeOptions>[];
  leaf: boolean;
  leafDescendants: Partial<ClientOptions>[];
  name: string;
  parent: Partial<ClientOptions> | null;
  parentId: string | null;
  planTypeClassifications: Partial<ClassificationOptions>[];
  ssoEnabled: boolean;
  isPayingCustomer: boolean;
  isContracted: boolean;
  enabledFlags: string[];
  managerGroupTypesInNetwork: Partial<GroupTypeOptions>[];
  importType: ImportType;
  productAccess: Partial<ProductAccess>;
  integrationTypes: IntegrationType[];
}

function getDefaults(): ClientOptions {
  return {
    id: '',
    children: [],
    clientType: '' as ClientType,
    clientGroupTypes: [],
    collaboratorIds: [],
    unownedCollaborations: [],
    episodeClassifications: [],
    enabledProviderTypes: [],
    groupTypeDisplayNames: [],
    groupTypes: [],
    leaf: true,
    leafDescendants: [],
    name: '',
    parent: null,
    parentId: null,
    planTypeClassifications: [],
    ssoEnabled: false,
    isPayingCustomer: false,
    isContracted: true,
    enabledFlags: [],
    managerGroupTypesInNetwork: [],
    importType: ImportType.IMPORTED_PATIENT,
    productAccess: {
      traditionalOlio: { enabled: true },
      olioSummarization: { enabled: false },
      referralSummarization: { enabled: false },
      dischargeCalendar: { enabled: false },
    },
    integrationTypes: [],
  };
}
/**
 *
 * @class Client
 * @classdesc Represents a client in the system
 * @property {string} id - The client's id
 * @property {Client[]} children - The client's children
 * @property {ClientType} clientType - The client's type
 * @property {ClientGroupType[]} clientGroupTypes - The client's group types
 * @property {string[]} collaboratorIds - The client's collaborator ids
 * @property {Classification[]} episodeClassifications - The client's episode classifications
 * @property {string[]} groupTypeDisplayNames - The client's group type display names
 * @property {GroupType[]} groupTypes - The client's group types
 * @property {boolean} leaf - Whether the client is a leaf
 * @property {Client[]} leafDescendants - The client's leaf descendants
 * @property {string} name - The client's name
 * @property {Client} parent - The client's parent
 * @property {string} parentId - The client's parent id
 * @property {Classification[]} planTypeClassifications - The client's plan type classifications
 * @property {boolean} ssoEnabled - Whether the client has SSO enabled
 * @property {string[]} enabledFlags - The client's enabled flags
 * @method {boolean} isAdmin - Whether the client is an admin
 * @method {boolean} isAffiliate - Whether the client is an affiliate
 * @method {boolean} isHealthSystem - Whether the client is a health system
 * @method {boolean} isInpatientPostAcute - Whether the client is inpatient post acute
 * @method {boolean} isOutpatientPostAcute - Whether the client is outpatient post acute
 * @method {boolean} isOwningClientType - Whether the client is an owning client type
 * @method {boolean} isPayor - Whether the client is a payor
 * @method {boolean} isParent - Whether the client is a parent
 * @method {boolean} isPhysicianGroup - Whether the client is a physician group
 * @method {string[]} leafDescendantIds - The client's leaf descendant ids
 * @method {GroupType[]} providerGroupTypes - The client's provider group types
 * @method {string[]} groupTypeIds - The client's group type ids
 * @method {object} toFormValues - The client's form values
 * @param {Partial<ClientOptions>} options - The client's options
 * @returns {Client} - A new client
 */

export default class Client {
  id: string;
  children: Client[];
  clientType: ClientType;
  clientGroupTypes: ClientGroupType[];
  collaboratorIds: string[];
  unownedCollaborations: Collaboration[];
  episodeClassifications: Classification[];
  enabledProviderTypes: GroupType[];
  groupTypeDisplayNames: string[];
  groupTypes: GroupType[];
  leaf: boolean;
  leafDescendants: Client[];
  name: string;
  parent: Client | null;
  parentId: string | null;
  planTypeClassifications: Classification[];
  ssoEnabled: boolean;
  isPayingCustomer: boolean;
  isContracted: boolean;
  enabledFlags: string[];
  managerGroupTypesInNetwork: GroupType[];
  importType: ImportType;
  productAccess: Partial<ProductAccess>;
  integrationTypes: IntegrationType[];

  constructor(options: Partial<ClientOptions> = {}) {
    const opts = { ...getDefaults(), ...options };
    this.id = opts.id;
    this.children = opts.children.map((child) => new Client(child));
    this.clientType = opts.clientType;
    this.collaboratorIds = opts.collaboratorIds;
    this.unownedCollaborations = opts.unownedCollaborations.map((uc) => new Collaboration(uc));
    this.clientGroupTypes = opts.clientGroupTypes.map((cgt) => new ClientGroupType(cgt));
    this.episodeClassifications = opts.episodeClassifications.map((ec) => new Classification(ec));
    this.enabledProviderTypes = opts.enabledProviderTypes.map((gt) => new GroupType(gt));
    this.groupTypeDisplayNames = opts.groupTypeDisplayNames;
    this.groupTypes = opts.groupTypes.map((gt) => new GroupType(gt));
    this.leaf = opts.leaf;
    this.leafDescendants = opts.leafDescendants.map((child) => new Client(child));
    this.name = opts.name;
    this.parent = opts.parent ? new Client(opts.parent) : null;
    this.parentId = opts.parentId;
    this.planTypeClassifications = opts.planTypeClassifications.map((p) => new Classification(p));
    this.ssoEnabled = opts.ssoEnabled;
    this.isPayingCustomer = opts.isPayingCustomer;
    this.isContracted = opts.isContracted;
    this.enabledFlags = opts.enabledFlags;
    this.managerGroupTypesInNetwork = opts.managerGroupTypesInNetwork.map((gt) => new GroupType(gt));
    this.importType = opts.importType;
    this.productAccess = opts.productAccess;
    this.integrationTypes = opts.integrationTypes ?? [];
  }

  get clientManagedGroupTypes() {
    return this.clientGroupTypes.filter((cgt) => cgt.isManaged);
  }
  get clientProvidedGroupTypes() {
    return this.clientGroupTypes.filter((cgt) => cgt.isProvided);
  }
  get isAdmin() {
    return this.clientType === ClientType.ADMIN;
  }
  get isAffiliate() {
    return this.clientType === ClientType.AFFILIATE;
  }
  get isHealthSystem() {
    return this.clientType === ClientType.HEALTH_SYSTEM;
  }
  get isOwningClientType() {
    return [ClientType.HEALTH_SYSTEM, ClientType.PAYOR, ClientType.PHYSICIAN_GROUP].includes(this.clientType);
  }
  get isPayor() {
    return this.clientType === ClientType.PAYOR;
  }
  get isParent() {
    return !this.leaf;
  }
  get isPhysicianGroup() {
    return this.clientType === ClientType.PHYSICIAN_GROUP;
  }
  get isSnfCustomer() {
    return this.managesAndProvidesGroupType(SKILLED_NURSING_FACILITY);
  }
  get leafDescendantIds() {
    return this.leafDescendants.map((leaf) => leaf.id);
  }
  get managedProviderTypes() {
    return this.enabledProviderTypes.filter((groupType) =>
      this.clientManagedGroupTypes.map((cgt) => cgt.groupTypeId).find((id) => id === groupType.id)
    );
  }
  get providedProviderTypes() {
    return this.enabledProviderTypes.filter((groupType) =>
      this.clientProvidedGroupTypes.map((cgt) => cgt.groupTypeId).find((id) => id === groupType.id)
    );
  }
  get groupTypeIds() {
    return this.groupTypes.map((groupType) => groupType.id);
  }

  get managerGroupTypesForFilters() {
    return this.managerGroupTypesInNetwork.filter((gt) => ![AFFILIATE, PHYSICIAN_TEAM].includes(gt.apiName));
  }

  get isChildCollaborator() {
    return this.unownedCollaborations.length > 0;
  }

  // A client that *sends* referrals — identified by a managed group type with the referral
  // care option on (the admin "Referral" checkbox in Configure Care Options). This is what
  // the outbound wording keys off; it cannot key off `handlesReferrals`, because a receiving
  // organization satisfies that too (OLIO-2905).
  get sendsReferrals() {
    return this.clientManagedGroupTypes.some((clientGroupType) => !!clientGroupType.config.referral);
  }

  // A client that *receives* referrals — identified by the provider types it provides.
  get receivesReferrals() {
    return !!this.providedProviderTypes.length;
  }

  // Either direction: what makes the referrals nav item and list page available at all.
  get handlesReferrals() {
    return this.receivesReferrals || this.sendsReferrals;
  }

  get referralSummarizationEnabled() {
    return !!this.productAccess.referralSummarization?.enabled;
  }

  get olioSummarizationEnabled() {
    return !!this.productAccess.olioSummarization?.enabled;
  }

  get careCoordinationEnabled() {
    return !!this.productAccess.traditionalOlio?.enabled;
  }

  get dischargeCalendarEnabled() {
    return !!this.productAccess.dischargeCalendar?.enabled;
  }

  get admissionsManagementEnabled() {
    return !!this.productAccess.admissions?.enabled;
  }

  get inboundReferralsEnabled() {
    return !!this.productAccess.inboundReferrals?.enabled;
  }

  get preferredProvidersEnabled() {
    return !!this.productAccess.preferredProviders?.enabled;
  }

  hasFlag(flag: string) {
    return this.enabledFlags.includes(flag);
  }

  toFormValues() {
    return {
      ...this,
      clientType: this.clientType ? { name: this.clientType, kind: this.clientType } : null,
    };
  }

  configForGroupType(groupTypeApiNameOrId: string) {
    return this.clientGroupTypes.find((clientGroupType) =>
      [clientGroupType.groupType?.apiName, clientGroupType.groupTypeId].includes(groupTypeApiNameOrId)
    )?.config;
  }

  configForManagedGroupType(groupTypeApiNameOrId: string) {
    return this.clientManagedGroupTypes.find((clientGroupType) =>
      [clientGroupType.groupType?.apiName, clientGroupType.groupTypeId].includes(groupTypeApiNameOrId)
    )?.config;
  }

  configForProvidedGroupType(groupTypeApiNameOrId: string) {
    return this.clientProvidedGroupTypes
      .filter((clientGroupType) => clientGroupType.isProvided)
      .find((clientGroupType) =>
        [clientGroupType.groupType?.apiName, clientGroupType.groupTypeId].includes(groupTypeApiNameOrId)
      )?.config;
  }

  managesAndProvidesGroupType(groupTypeApiNameOrId: string): boolean {
    return [this.managedProviderTypes, this.providedProviderTypes].every((groupTypes) =>
      groupTypes.some(
        (groupType) => groupType.id === groupTypeApiNameOrId || groupType.apiName === groupTypeApiNameOrId
      )
    );
  }
}
